import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Box, Typography, Button, Paper, TextField, Snackbar, Alert } from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import html2canvas from "html2canvas";
import { LabelList } from "recharts";
import apiService from "../services/apiService";
import EmailConfigModal from "../Components/EmailConfigModal";

const formatDateMMDDYYYY = (value) => {
  if (value === "Total") return "Total";

  const d = new Date(value);

  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

// Color constants - matching Garpac reports
const BLUE_PRIMARY = "#1f5582";
const BLUE_CARD = "#2e75b6";
const ORANGE_ACCENT = "#ff7f0e";
const WHITE_BG = "#ffffff";
const BORDER_GRAY = "#d0d7e0";
const LIGHT_BG = "#f8fafc";

const hasValue = (data, key) => data?.some((item) => Number(item[key]) > 0);
const chartShadowStyle = {
  border: "2px solid #939090",
  borderRadius: 3,
  backgroundColor: "#FFFFFF",
  boxShadow: `
      8px 8px 8px rgba(0, 0, 0, 0.15),
      8px 8px 16px rgba(0, 0, 0, 0.08)
    `,
};

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return d.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
const BoomiDashboard = forwardRef((props, ref) => {
  const {
    period = "daily",
    dateType = "reported",
    fromDate: propFromDate,
    toDate: propToDate,
    envId = "",
    appId = "",
    showEmailSubject: propShowEmailSubject,
    setShowEmailSubject: propSetShowEmailSubject,
    emailConfigOpen: propEmailConfigOpen,
    setEmailConfigOpen: propSetEmailConfigOpen,
  } = props;

  const dashboardRef = useRef();
  const [data, setData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ open: false, message: '', severity: 'info' });
  const [customEmailSubject, setCustomEmailSubject] = useState("");

  // Use props if provided, otherwise use internal state
  const [internalShowEmailSubject, setInternalShowEmailSubject] = useState(false);
  const [internalEmailConfigOpen, setInternalEmailConfigOpen] = useState(false);

  const showEmailSubject = propShowEmailSubject !== undefined ? propShowEmailSubject : internalShowEmailSubject;
  const setShowEmailSubject = propSetShowEmailSubject || setInternalShowEmailSubject;
  const emailConfigOpen = propEmailConfigOpen !== undefined ? propEmailConfigOpen : internalEmailConfigOpen;
  const setEmailConfigOpen = propSetEmailConfigOpen || setInternalEmailConfigOpen;

  // Expose download and email methods to parent
  useImperativeHandle(ref, () => ({
    downloadDashboard: download,
    sendEmail: sendReportEmail,
  }));

  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatLocalDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    return {
      from: formatLocalDate(firstDay),
      to: formatLocalDate(lastDay),
    };
  };
  const colors = [
    BLUE_PRIMARY,
    ORANGE_ACCENT,
    BLUE_CARD,
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff8042",
    "#a4de6c",
  ];
  const cleanedActions =
    data?.weeklyData?.actions?.filter(
      (a) =>
        a.action && // not null
        a.action.trim() !== "" && // not empty
        a.action.toLowerCase() !== "no action",
    ) || [];

  const ACTION_COLORS = [
    BLUE_PRIMARY,
    ORANGE_ACCENT,
    BLUE_CARD,
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff8042",
  ];

  const download = async () => {
    setIsDownloading(true);
    await new Promise((r) => setTimeout(r, 300));

    const element = dashboardRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
    });
    const link = document.createElement("a");
    
    // Generate filename based on application name and period
    const appName = data?.applicationName || 'Boomi';
    const fileName = `${appName.toLowerCase().replace(/\s+/g, '_')}_${period}_report.png`;
    
    link.download = fileName;
    link.href = canvas.toDataURL("image/png");
    link.click();

    setIsDownloading(false);
  };

  const sendReportEmail = async () => {
    if (!dashboardRef.current) return;
    
    setSendingEmail(true);
    setEmailStatus({ open: false, message: '', severity: 'info' });

    try {
      // Prepare report data
      const reportData = {
        title: data?.applicationName 
          ? `${data.applicationName} ${period.charAt(0).toUpperCase() + period.slice(1)} Dashboard` 
          : `Boomi ${period.charAt(0).toUpperCase() + period.slice(1)} Dashboard`,
        period: period,
        dateType: dateType,
        fromDate: fromDate,
        toDate: toDate,
        envId: envId,
        appId: appId,
        highlights: highlights,
        kpis: data?.kpis || {}
      };

      try {
        // First, try to send with image
        const canvas = await html2canvas(dashboardRef.current, {
          scale: 1.5,
          useCORS: true,
          quality: 0.8,
          backgroundColor: '#ffffff'
        });
        
        const imageData = canvas.toDataURL("image/jpeg", 0.8);

        // Check if image is too large
        const imageSizeKB = Math.round(imageData.length / 1024);
        

        if (imageSizeKB > 5000) {
          throw new Error('Image too large, using text-only email');
        }

        // Send email with image - use period (daily, weekly, monthly) instead of boomi_period
        const response = await apiService.post('/email/send-report', {
          reportType: period,
          reportData,
          imageData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || `${period.charAt(0).toUpperCase() + period.slice(1)} report sent successfully with image!`,
          severity: 'success'
        });

      } catch (imageError) {
        
        
        // Fallback to text-only email
        const response = await apiService.post('/email/send-report-text', {
          reportType: period,
          reportData,
          customSubject: customEmailSubject
        });

        setEmailStatus({
          open: true,
          message: response.message || `${period.charAt(0).toUpperCase() + period.slice(1)} report sent successfully (text format)!`,
          severity: 'success'
        });
      }

    } catch (error) {
      console.error('Error sending email:', error);
      
      let errorMessage = 'Failed to send email. Please check your email configuration.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
        
        if (errorMessage.includes('No email configuration found')) {
          errorMessage += ' Click the settings icon (⚙️) to configure email recipients.';
        } else if (errorMessage.includes('Email configuration is disabled')) {
          errorMessage = 'Email is configured but disabled. Click the settings icon (⚙️) to enable email sending.';
        }
      }
      
      setEmailStatus({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSendingEmail(false);
    }
  };
  const getCurrentWeekRange = () => {
    const curr = new Date();
    const firstDay = new Date(curr.setDate(curr.getDate() - curr.getDay())); // Sunday
    const lastDay = new Date(curr.setDate(curr.getDate() - curr.getDay() + 6)); // Saturday

    const formatDate = (d) => d.toISOString().split("T")[0]; // YYYY-MM-DD
    const getCurrentMonthRange = () => {
      const now = new Date();

      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const format = (d) => d.toISOString().split("T")[0];

      return {
        from: format(firstDay),
        to: format(lastDay),
      };
    };
    return {
      from: formatDate(firstDay),
      to: formatDate(lastDay),
    };
  };

  const [selectedProcess, setSelectedProcess] = useState("");
  const [failureInput, setFailureInput] = useState("");
  const [successInput, setSuccessInput] = useState("");
  const [highlights, setHighlights] = useState("");

  // Use props for fromDate and toDate
  const fromDate = propFromDate;
  const toDate = propToDate;

  // Update custom email subject when period or dates change
  useEffect(() => {
    const generateDefaultSubject = () => {
      const appName = data?.applicationName || 'Boomi';
      const periodName = period.charAt(0).toUpperCase() + period.slice(1);
      
      if (fromDate && toDate) {
        const formatDate = (dateStr) => {
          const d = new Date(dateStr + 'T00:00:00');
          return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        };
        
        if (fromDate === toDate) {
          return `${periodName} Report - ${appName} Job Monitoring - ${formatDate(fromDate)}`;
        } else {
          return `${periodName} Report - ${appName} Job Monitoring - ${formatDate(fromDate)} to ${formatDate(toDate)}`;
        }
      }
      
      return `${periodName} Report - ${appName} Job Monitoring`;
    };

    const defaultSubject = generateDefaultSubject();
    setCustomEmailSubject(defaultSubject);
  }, [period, fromDate, toDate, data?.applicationName]);

  useEffect(() => {
    let apiFromDate = fromDate;
    let apiToDate = toDate;

   

    if (apiFromDate && apiToDate) {
      const url = `/reports/boomi?period=${period}&date_type=${dateType}&from=${apiFromDate}&to=${apiToDate}&env_id=${envId}&app_id=${appId}`;
      
      
      apiService
        .get(url)
        .then((res) => {
          
          setData(res);
        })
        .catch((err) => {
          console.error("BoomiDashboard - API error:", err);
          console.error("BoomiDashboard - Error details:", err.response?.data);
          setData(null);
        });
    } else {
     
    }
  }, [period, dateType, fromDate, toDate, envId, appId]);

  // Fetch highlights when dates change
  useEffect(() => {
    const fetchHighlights = async () => {
      if (!fromDate || !toDate) return;
      
      try {
        const endpoint = period === 'weekly' 
          ? '/boomi-weekly-report/get-weekly-highlights'
          : '/boomi-monthly-report/get-monthly-highlights';
        
        const params = new URLSearchParams({
          date_from: fromDate,
          date_to: toDate
        });

        // Only add application if appId is provided
        if (appId) {
          params.append('application', appId);
        }

        const data = await apiService.get(`${endpoint}?${params}`);
        setHighlights(data.highlights || "");
      } catch (error) {
        console.error("Error fetching highlights:", error);
        setHighlights("");
      }
    };

    fetchHighlights();
  }, [fromDate, toDate, period, appId]);

  // Save highlights function
  const saveHighlights = async () => {
    if (!fromDate || !toDate) return;
    
    try {
      const endpoint = period === 'weekly'
        ? '/boomi-weekly-report/save-weekly-highlights'
        : '/boomi-monthly-report/save-monthly-highlights';
      
      const title = data?.applicationName 
        ? `${data.applicationName} ${period === 'weekly' ? 'Weekly' : 'Monthly'} Report`
        : `Boomi ${period === 'weekly' ? 'Weekly' : 'Monthly'} Report`;

      const payload = {
        title,
        highlights,
        date_from: fromDate,
        date_to: toDate
      };

      // Only add application if appId is provided
      if (appId) {
        payload.application = appId;
      }

      await apiService.post(endpoint, payload);
     
    } catch (error) {
      console.error("Error saving highlights:", error);
    }
  };

  if (!data) return <>Loading...</>;
  // const criticalJobChartData = data?.dailyCriticalJobs || [];
  const criticalJobChartData = (data?.dailyCriticalJobs || []).map((job) => ({
    client_job_id: job.client_job_id,
    failure: job.failure_count, // rename
    success: job.success_count, // rename
  }));
  const kpis = data?.kpis ?? {};

  const charts = {
    folder: data?.charts?.folder ?? [],
    processAlert: data?.charts?.processAlert ?? [],
    endpoint: data?.charts?.endpoint ?? [],
    endpointFolders: data?.charts?.endpointFolders ?? [],
  };
  const weeklyData = data?.weeklyData ?? {};

  const weeklyFolders = weeklyData.folders ?? [];
  const weeklyDateFolder = weeklyData.dateFolder ?? [];
  const weeklyDateOccurrence = weeklyData.dateOccurrence ?? [];
  const monthlyData = data?.monthlyData ?? {};

  const commonPaperStyle = {
    border: "3px solid #939090",
    borderRadius: 3,
  };

  return (
    <Box p={2}>
      {/* Email Subject Input - Controlled from container */}
      {showEmailSubject && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="Email Subject"
            value={customEmailSubject}
            onChange={(e) => setCustomEmailSubject(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
                '&:hover fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
                '&.Mui-focused fieldset': {
                  borderColor: BLUE_PRIMARY,
                },
              },
              '& .MuiInputLabel-root': {
                color: BLUE_PRIMARY,
                '&.Mui-focused': {
                  color: BLUE_PRIMARY,
                },
              },
            }}
          />
        </Box>
      )}

      {/* EXPORT AREA */}
      <Box 
        ref={dashboardRef} 
        p={2.5} 
        bgcolor="#fff"
        sx={{
          border: `2px solid ${BLUE_PRIMARY}`,
          borderRadius: 2,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          '& *': {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          }
        }}
      >
        <Typography 
          variant="h5" 
          align="center" 
          mb={0.5} 
          fontWeight="bold" 
          color={BLUE_PRIMARY}
          sx={{ fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
        >
          {data?.applicationName 
            ? `${data.applicationName} ${period.charAt(0).toUpperCase() + period.slice(1)} Dashboard` 
            : `Boomi ${period.charAt(0).toUpperCase() + period.slice(1)} Dashboard`}
        </Typography>

        {/* KPI */}

        {period === "daily" && (
          <>
            <Paper
              sx={{
                p: 0.5,
                mb: 0.5,
                mt: 0.5,
                textAlign: "center",
                // border: "2px solid #847e7e",
                // borderRadius: 4,
                backgroundColor: "#FFFFFF",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <Box
                display="grid"
                gridTemplateColumns="repeat(8,1fr)"
                gap={0.5}
                sx={{
                  "& > .MuiPaper-root": {
                    borderRadius: 4,
                  },
                }}
              >
                {[
                  ["Total", kpis.total ?? 0],
                  ["Event Listener", kpis.eventListener],
                  ["Scheduled", kpis.scheduled],
                  ["High", kpis.high],
                  ["Medium", kpis.medium],
                  ["Low", kpis.low],
                  ["Data Error", kpis.dataError],
                  ["System Error", kpis.systemError],
                ].map(([l, v], i) => (
                  <Paper
                    key={i}
                    sx={{
                      p: 0.5,
                      textAlign: "center",
                      border: "2px solid #939090",
                    }}
                  >
                    <Typography fontSize={16} fontWeight="bold" color="#030303">
                      {l}
                    </Typography>
                    <Typography fontWeight="bold">{v || 0}</Typography>
                  </Paper>
                ))}
              </Box>
            </Paper>

            <Box display="grid" gridTemplateColumns="30% 69.5%" gap={0.5}>
              <Paper
                sx={{
                  ...chartShadowStyle,
                  p: 1,
                  height: 300,
                  border: "2px solid #939090",
                  borderRadius: 5,
                  boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize={14}
                  mb={1}
                  color="#212529"
                  sx={{ textAlign: "center" }}
                >
                  Occurrence by Folder
                </Typography>

                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={charts.folder}>
                    <XAxis
                      dataKey="folder"
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      axisLine={false}
                      tickLine={false}
                    />

                    <Tooltip />

                    {(hasValue(charts.folder, "high") ||
                      hasValue(charts.folder, "medium") ||
                      hasValue(charts.folder, "low")) && (
                      <Legend
                        verticalAlign="top"
                        height={30}
                        wrapperStyle={{ top: -5 }}
                        iconType="circle"
                        formatter={(value) =>
                          value.charAt(0).toUpperCase() +
                          value.slice(1).toLowerCase()
                        }
                      />
                    )}

                    <Bar dataKey="high" stackId="a" fill={BLUE_PRIMARY}>
                      <LabelList dataKey="high" position="top" style={{ fontSize: 9 }} />
                    </Bar>

                    <Bar dataKey="medium" stackId="a" fill={ORANGE_ACCENT}>
                      <LabelList dataKey="medium" position="top" style={{ fontSize: 9 }} />
                    </Bar>

                    <Bar dataKey="low" stackId="a" fill={BLUE_CARD}>
                      <LabelList dataKey="low" position="top" style={{ fontSize: 9 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              <Paper
                sx={{
                  ...chartShadowStyle,
                  p: 1,
                  height: 300,
                  border: "2px solid #939090",
                  borderRadius: 4,
                  boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize={14}
                  mb={1}
                  color="#212529"
                  align="center"
                >
                  Alerts by Process
                </Typography>

                <ResponsiveContainer width="100%" height="90%">
                  <BarChart
                    data={charts.processAlert}
                    margin={{ top: 10, right: 20, left: -10, bottom: 10 }}
                  >
                    <XAxis
                      dataKey="process"
                      angle={-30}
                      textAnchor="end"
                      interval={0}
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      axisLine={false}
                      tickLine={false}
                    />

                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        fontSize: 12,
                      }}
                    />

                    <Legend
                      wrapperStyle={{ top: -5 }}
                      verticalAlign="top"
                      height={30}
                      iconType="circle"
                      formatter={(value) =>
                        value.charAt(0).toUpperCase() +
                        value.slice(1).toLowerCase()
                      }
                    />

                    {hasValue(charts.processAlert, "incident") && (
                      <Bar dataKey="incident" stackId="a" fill={BLUE_PRIMARY}>
                        <LabelList dataKey="incident" position="top" style={{ fontSize: 9 }} />
                      </Bar>
                    )}

                    {hasValue(charts.processAlert, "no_action") && (
                      <Bar dataKey="no_action" stackId="a" fill={BLUE_CARD}>
                        <LabelList dataKey="no_action" position="top" style={{ fontSize: 9 }} />
                      </Bar>
                    )}

                    {hasValue(charts.processAlert, "email") && (
                      <Bar dataKey="email" stackId="a" fill={ORANGE_ACCENT}>
                        <LabelList dataKey="email" position="top" style={{ fontSize: 9 }} />
                      </Bar>
                    )}

                    {hasValue(charts.processAlert, "sev1") && (
                      <Bar dataKey="sev1" stackId="a" fill="#8884d8">
                        <LabelList dataKey="sev1" position="top" style={{ fontSize: 9 }} />
                      </Bar>
                    )}

                    {hasValue(charts.processAlert, "rerun") && (
                      <Bar dataKey="rerun" stackId="a" fill="#82ca9d">
                        <LabelList dataKey="rerun" position="top" style={{ fontSize: 9 }} />
                      </Bar>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="  69.5% 30%"
              gap={0.5}
              mt={0.5}
            >
              <Paper
                sx={{
                  ...chartShadowStyle,
                  p: 1,
                  height: 300,
                  border: "2px solid #939090",
                  borderRadius: 4,
                  boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize={14}
                  color="#212529"
                  align="center"
                >
                  Critical Jobs
                </Typography>

                <ResponsiveContainer width="100%" height="95%">
                  <BarChart
                    data={criticalJobChartData}
                    margin={{ top: 20, right: 20, left: 20, bottom: 15 }}
                  >
                    <XAxis
                      dataKey="client_job_id"
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) =>
                        value?.length > 9 ? value.slice(0, 9) : value
                      }
                    />
                    <YAxis
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, "dataMax + 80"]}
                    />
                    <Tooltip />

                    {(hasValue(criticalJobChartData, "failure") ||
                      hasValue(criticalJobChartData, "success")) && (
                      <Legend
                        verticalAlign="top"
                        align="left"
                        height={20}
                        iconType="circle"
                        wrapperStyle={{ 
                          top: 6, 
                          left: 20,
                          paddingLeft: '20px',
                          textAlign: 'left',
                          width: '100%'
                        }}
                        payload={[
                          { value: 'POS', type: 'circle', color: BLUE_PRIMARY },
                          { value: 'DOM', type: 'circle', color: BLUE_CARD }
                        ]}
                      />
                    )}

                    <Bar dataKey="failure" stackId="a" fill={BLUE_PRIMARY}>
                      <LabelList
                        dataKey="failure"
                        position="center"
                        fontSize={9}
                      />
                    </Bar>

                    <Bar dataKey="success" stackId="a" fill={BLUE_CARD}>
                      <LabelList
                        dataKey="success"
                        position="top"
                        fontSize={9}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              <Paper
                sx={{
                  ...chartShadowStyle,
                  p: 1,
                  height: 300,
                  border: "2px solid #939090",
                  borderRadius: 3,
                  boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize={14}
                  mb={1}
                  color="#212529"
                  align="center"
                >
                  Occurrence by Endpoint
                </Typography>

                <ResponsiveContainer width="100%" height="95%">
                  <BarChart
                    layout="vertical"
                    data={charts.endpoint}
                    margin={{ top: 20, right: 20, left: -130, bottom: 15 }}
                  >
                    <XAxis
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      type="number"
                      axisLine={false}
                      tickLine={false}
                      domain={[0, "dataMax + 50"]}
                    />

                    <YAxis
                      type="category"
                      dataKey="end_point"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                      width={220}
                    />

                    <Tooltip />

                    <Legend
                      verticalAlign="top"
                      align="right"
                      height={20}
                      iconType="circle"
                      wrapperStyle={{ top: -6 }}
                    />
                    {charts.endpointFolders.map((folder, i) => (
                      <Bar
                        key={folder}
                        dataKey={folder}
                        stackId="a"
                        fill={colors[i % colors.length]}
                      >
                        <LabelList
                          dataKey={folder}
                          position="right"
                          fontSize={9}
                          fill="#000"
                        />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </>
        )}
        {period === "weekly" && data?.weekly && (
          <>
            <Box display="grid" gridTemplateColumns="33% 33% 33%" gap={0.5}>
              <Paper
                sx={{
                  ...commonPaperStyle,
                  p: 1,
                  height: 300,
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize={14}
                  mb={0.5}
                  color="#212529"
                >
                  Received Date vs Folder Count
                </Typography>

                <ResponsiveContainer width="100%" height="80%">
                  <BarChart
                    data={data.weeklyData.dateFolder}
                    margin={{ top: 20, right: 20, left: 0, bottom: 40 }}
                  >
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fontWeight: "bold", angle: -45, textAnchor: "end" }}
                      tickFormatter={formatDateMMDDYYYY}
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 11, fontWeight: "bold" }} />
                    <Tooltip labelFormatter={formatDateMMDDYYYY} />

                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="circle"
                      wrapperStyle={{
                        fontSize: 11,
                        paddingBottom: 20,
                      }}
                    />

                    {weeklyFolders.map((f, i) => {
                      const isLast = i === data.weeklyData.folders.length - 1;

                      return (
                        <Bar
                          key={f}
                          dataKey={f}
                          stackId="a"
                          fill={colors[i % colors.length]}
                        >
                          {isLast && (
                            <LabelList
                              position="top"
                              formatter={(value) => (value > 0 ? value : "")}
                              style={{
                                fontSize: 9,
                                fontWeight: "bold",
                                fill: "#212529",
                              }}
                            />
                          )}
                        </Bar>
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              <Paper
                sx={{
                  ...commonPaperStyle,
                  p: 1,
                  height: 300,
                  overflow: "auto",
                }}
              >
                <Typography fontWeight="bold" fontSize={14} mb={1}>
                  Received Date (PST) vs Occurrence
                </Typography>

                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #939090" }}>
                      <th
                        style={{
                          textAlign: "left",
                          fontSize: 12,
                          padding: "6px",
                        }}
                      >
                        Received Date (PST)
                      </th>
                      <th
                        style={{
                          textAlign: "right",
                          fontSize: 12,
                          padding: "6px",
                        }}
                      >
                        Sum of Occurrence
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {[
                      ...data.weeklyData.dateOccurrence,
                      {
                        received_dt: "Total",
                        total_occurrence: data.weeklyData.dateOccurrence.reduce(
                          (sum, r) => sum + r.total_occurrence,
                          0,
                        ),
                      },
                    ].map((row, i, arr) => {
                      const max = Math.max(
                        ...arr
                          .filter((r) => r.received_dt !== "Total")
                          .map((r) => r.total_occurrence),
                      );

                      const isTotal = row.received_dt === "Total";

                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #ddd" }}>
                          {/* DATE COLUMN */}
                          <td
                            style={{
                              fontSize: 12,
                              fontWeight: isTotal ? "bold" : "normal",
                              padding: "6px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isTotal
                              ? "Total"
                              : formatDateMMDDYYYY(row.received_dt)}
                          </td>

                          {/* BAR + VALUE COLUMN */}
                          <td style={{ padding: "6px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              {!isTotal && (
                                <div
                                  style={{
                                    height: 14,
                                    width: `${(row.total_occurrence / max) * 100}%`,
                                    backgroundColor: BLUE_PRIMARY,
                                    borderRadius: 2,
                                  }}
                                />
                              )}

                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: isTotal ? "bold" : "normal",
                                  marginLeft: "auto",
                                }}
                              >
                                {row.total_occurrence}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Paper>

              <Paper
                sx={{
                  ...commonPaperStyle,
                  p: 1,
                  height: 300,
                }}
              >
                <Typography
                  fontWeight="bold"
                  fontSize={14}
                  mb={1}
                  align="center"
                >
                  Actions taken
                </Typography>

                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={cleanedActions}
                      dataKey="action_count"
                      nameKey="action"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={2}
                      label={({ value, percent }) =>
                        `${value} (${Math.round(percent * 100)}%)`
                      }
                    >
                      {cleanedActions.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={ACTION_COLORS[index % ACTION_COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip />

                    <Legend
                      verticalAlign="top"
                      align="left"
                      iconType="circle"
                      wrapperStyle={{
                        fontSize: 11,
                        paddingBottom: 18,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>

            <Paper sx={{ ...commonPaperStyle, mt: 0.5, p: 1 }}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  color: "#0D47A1",
                }}
              >
                Highlights:
              </Typography>
              {!isDownloading ? (
                <TextField
                  multiline
                  rows={5}
                  fullWidth
                  placeholder="Weekly highlights..."
                  value={highlights}
                  onChange={(e) => setHighlights(e.target.value)}
                  onBlur={saveHighlights}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "14px",
                      color: "#1a237e",
                      lineHeight: 1.6,
                      fontWeight: 500,
                    },
                    "& textarea": { backgroundColor: "#f9fafc" },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#00020e",
                    lineHeight: 1.6,
                  }}
                >
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    {highlights
                      ?.split("\n")
                      .filter((line) => line.trim() !== "")
                      .map((line, index) => (
                        <li key={index}>{line}</li>
                      ))}
                  </ul>
                </Typography>
              )}
            </Paper>
          </>
        )}
        {period === "monthly" && data?.monthly && (
          <>
            <Paper
              sx={{
                p: 0.5,
                mb: 0.5,
                mt: 0.5,
                textAlign: "center",
              
                backgroundColor: "#FFFFFF",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
             
               <Box
                display="grid"
                gridTemplateColumns="repeat(8,1fr)"
                gap={0.5}
                sx={{
                  "& > .MuiPaper-root": {
                    borderRadius: 4,
                  },
                }}
              >
                {[
                  ["Total Occurrence", monthlyData.kpis?.total],
                  ["Scheduled", monthlyData.kpis?.scheduled],
                  ["Listener", monthlyData.kpis?.listener],
                  ["System Error", monthlyData.kpis?.system_error],
                  ["Data Error", monthlyData.kpis?.data_error],
                  [
                    "Critical Jobs Failures",
                    monthlyData.kpis?.critical_job_failure,
                  ],
                  ["Missing Playbook", monthlyData.kpis?.missing_playbook], // new
                  ["Tickets Raised", monthlyData.kpis?.ticket_raised], // new
                ].map(([label, value], i) => (
                  <Paper
                    key={i}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      border: "2px solid #939090",
                    }}
                  >
                    <Typography fontWeight="bold" fontSize={14}>
                      {label}
                    </Typography>
                    <Typography fontWeight="bold">{value || 0}</Typography>
                  </Paper>
                ))}
              </Box>
            </Paper>

            {/*  Charts */}
           
            <Box
                display="grid"
                gridTemplateColumns="repeat(3,1fr)"
                gap={0.5}
                sx={{
                  "& > .MuiPaper-root": {
                    borderRadius: 4,
                  },
                }}
              >
              <Paper
                sx={{
                  ...commonPaperStyle,
                  p: 1,
                  height: 300,
                }}
              >
                <Typography fontWeight="bold">Alerts by Folder</Typography>

                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthlyData.folder}>
                    <XAxis
                      dataKey="folder"
                      tick={{ fontSize: 11, fontWeight: "bold" }}
                    />
                    <YAxis tick={{ fontSize: 11, fontWeight: "bold" }} />
                    <Tooltip />

                    <Bar dataKey="count">
                      {monthlyData.folder.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={colors[index % colors.length]}
                        />
                      ))}

                      <LabelList position="top" style={{ fontSize: 9 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              {/* Action Taken Donut */}
              <Paper
                sx={{
                  ...commonPaperStyle,
                  p: 1,
                  height: 300,
                }}
              >
                <Typography fontWeight="bold" align="left">
                  Action Taken
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={monthlyData.actions}
                      dataKey="action_count"
                      nameKey="action"
                      innerRadius={70}
                      outerRadius={100}
                      label
                    >
                      {monthlyData.actions?.map((_, i) => (
                        <Cell
                          key={i}
                          fill={ACTION_COLORS[i % ACTION_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>

              <Paper
                sx={{
                  ...commonPaperStyle,
                  p: 1,
                  height: 300,
                }}
              >
                <Typography fontWeight="bold" align="left">
                  Alerts by SFSC & SessionM
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={monthlyData.sfscSessionM}
                      dataKey="total"
                      nameKey="end_point"
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {monthlyData.sfscSessionM.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === 0 ? BLUE_PRIMARY : ORANGE_ACCENT}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>

            <Paper sx={{ ...commonPaperStyle, mt: 0.5, p: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  color: "#0D47A1",
                }}
              >
                Highlights:
              </Typography>
              {!isDownloading ? (
                <TextField
                  multiline
                  rows={5}
                  fullWidth
                  placeholder="Weekly highlights..."
                  value={highlights}
                  onChange={(e) => setHighlights(e.target.value)}
                  onBlur={saveHighlights}
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "14px",
                      color: "#1a237e",
                      lineHeight: 1.6,
                      fontWeight: 500,
                    },
                    "& textarea": { backgroundColor: "#f9fafc" },
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#00020e",
                    lineHeight: 1.6,
                  }}
                >
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    {highlights
                      ?.split("\n")
                      .filter((line) => line.trim() !== "")
                      .map((line, index) => (
                        <li key={index}>{line}</li>
                      ))}
                  </ul>
                </Typography>
              )}
            </Paper>
          </>
        )}
      </Box>
      
      {/* Email Configuration Modal */}
      <EmailConfigModal 
        reportType={`boomi_${period}`}
        applicationName={data?.applicationName ? `${data.applicationName} ${period.charAt(0).toUpperCase() + period.slice(1)} Report` : null}
        open={emailConfigOpen} 
        onClose={() => setEmailConfigOpen(false)} 
      />

      {/* Email Status Snackbar */}
      <Snackbar
        open={emailStatus.open}
        autoHideDuration={6000}
        onClose={() => setEmailStatus({ ...emailStatus, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setEmailStatus({ ...emailStatus, open: false })} 
          severity={emailStatus.severity}
          sx={{ width: '100%' }}
        >
          {emailStatus.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default BoomiDashboard;
